import * as React from "react"
import { cn } from "@/lib/utils"

/**
 * NumericInput - A numeric input that auto-clears zero values on focus
 * When the field contains 0 or "0" and user focuses, it clears automatically
 * When user leaves the field empty, it restores to 0
 */
const NumericInput = React.forwardRef(({ 
  className, 
  value, 
  onChange, 
  onFocus,
  onBlur,
  clearOnZero = true,
  ...props 
}, ref) => {
  // Use local state to control the displayed value
  const [displayValue, setDisplayValue] = React.useState(value);
  const [isFocused, setIsFocused] = React.useState(false);
  
  // Sync with external value when not focused
  React.useEffect(() => {
    if (!isFocused) {
      setDisplayValue(value);
    }
  }, [value, isFocused]);

  const isZeroValue = (val) => {
    if (val === 0 || val === "0" || val === "0.00" || val === "" || val === null || val === undefined) {
      return true;
    }
    if (typeof val === 'string' && val.trim() === "") {
      return true;
    }
    const numVal = parseFloat(val);
    return typeof numVal === 'number' && !isNaN(numVal) && numVal === 0;
  };

  const handleFocus = (e) => {
    setIsFocused(true);
    
    // Clear the field if value is zero
    if (clearOnZero && isZeroValue(displayValue)) {
      setDisplayValue("");
      // Notify parent with empty value
      if (onChange) {
        const syntheticEvent = {
          ...e,
          target: { ...e.target, value: "" }
        };
        onChange(syntheticEvent);
      }
    }
    
    // Select all text for easy replacement
    e.target.select();
    onFocus?.(e);
  };

  const handleBlur = (e) => {
    setIsFocused(false);
    
    // Restore 0 if field is empty or just whitespace
    const currentValue = e.target.value;
    if (currentValue === "" || currentValue.trim() === "") {
      setDisplayValue("0");
      if (onChange) {
        const syntheticEvent = {
          ...e,
          target: { ...e.target, value: "0" }
        };
        onChange(syntheticEvent);
      }
    }
    onBlur?.(e);
  };

  const handleChange = (e) => {
    setDisplayValue(e.target.value);
    onChange?.(e);
  };

  return (
    <input
      type="number"
      className={cn(
        "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        className
      )}
      ref={ref}
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      {...props}
    />
  );
});

NumericInput.displayName = "NumericInput"

export { NumericInput }
